import { Check, Copy, Eye, EyeOff, Plus, Trash2, X } from "lucide-react";
import { useRef, useState } from "react";
import { notify } from "../../../lib/notify";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { BulkPasteModal } from "./BulkPasteModal";
import { isSensitive, validateKey } from "./utils";

// ── Types ────────────────────────────────────────────────────────────────────

export interface EnvVarEditorProps {
  /** Initial key-value map. The editor is uncontrolled — pass a new `key` prop
   *  on the parent to reset it when the backing data changes (e.g. after an API reload). */
  defaultValue?: Record<string, string>;
  /** Called with the full current map on every change. */
  onChange?: (vars: Record<string, string>) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export function EnvVarEditor({
  defaultValue = {},
  onChange,
}: EnvVarEditorProps) {
  const [vars, setVars] = useState<Record<string, string>>(defaultValue);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [newRow, setNewRow] = useState<{ key: string; value: string } | null>(
    null
  );
  const [newKeyError, setNewKeyError] = useState<string | null>(null);
  const [showBulkPaste, setShowBulkPaste] = useState(false);

  const newKeyRef = useRef<HTMLInputElement>(null);
  const newValueRef = useRef<HTMLInputElement>(null);

  function emit(next: Record<string, string>) {
    setVars(next);
    onChange?.(next);
  }

  // ── Row edit handlers ────────────────────────────────────────────────────

  function startEdit(key: string) {
    setEditingKey(key);
    setEditingValue(vars[key] ?? "");
  }

  function cancelEdit() {
    setEditingKey(null);
    setEditingValue("");
  }

  function confirmEdit(key: string) {
    emit({ ...vars, [key]: editingValue });
    setEditingKey(null);
  }

  function deleteKey(key: string) {
    const next = { ...vars };
    delete next[key];
    emit(next);
    if (editingKey === key) cancelEdit();
  }

  function toggleReveal(key: string) {
    setRevealed((r) => {
      const next = new Set(r);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function copyValue(value: string) {
    navigator.clipboard
      .writeText(value)
      .then(() => notify.success("Copied to clipboard"));
  }

  // ── New row handlers ─────────────────────────────────────────────────────

  function openNewRow() {
    setNewRow({ key: "", value: "" });
    setNewKeyError(null);
    setTimeout(() => newKeyRef.current?.focus(), 50);
  }

  function cancelNewRow() {
    setNewRow(null);
    setNewKeyError(null);
  }

  function confirmNewRow() {
    if (!newRow) return;
    const keyErr = validateKey(newRow.key);
    if (keyErr) {
      setNewKeyError(keyErr);
      newKeyRef.current?.focus();
      return;
    }
    emit({ ...vars, [newRow.key]: newRow.value });
    setNewRow(null);
    setNewKeyError(null);
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const entries = Object.entries(vars).sort(([a], [b]) => a.localeCompare(b));
  const isEmpty = entries.length === 0 && !newRow;

  return (
    <div>
      {showBulkPaste && (
        <BulkPasteModal
          onImport={(imported) => emit({ ...vars, ...imported })}
          onClose={() => setShowBulkPaste(false)}
        />
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-muted-foreground">
          {entries.length > 0
            ? `${entries.length} variable${entries.length !== 1 ? "s" : ""}`
            : "No variables yet"}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowBulkPaste(true)}
          >
            Paste .env
          </Button>
          <Button
            size="sm"
            className="gap-1.5"
            onClick={openNewRow}
            disabled={!!newRow}
          >
            <Plus className="h-3.5 w-3.5" />
            Add variable
          </Button>
        </div>
      </div>

      {/* New row input — appears above the table */}
      {newRow && (
        <div className="rounded-lg bg-muted/20 px-4 py-4 mb-3 space-y-3">
          <p className="text-xs font-medium text-muted-foreground">
            New variable
          </p>
          <div className="flex gap-3 items-start">
            <div className="flex-1 space-y-1">
              <label className="text-2xs text-muted-foreground uppercase tracking-wide">
                Key
              </label>
              <Input
                ref={newKeyRef}
                value={newRow.key}
                onChange={(e) => {
                  setNewRow({ ...newRow, key: e.target.value.toUpperCase() });
                  setNewKeyError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Tab") {
                    e.preventDefault();
                    newValueRef.current?.focus();
                  }
                  if (e.key === "Escape") cancelNewRow();
                }}
                placeholder="VARIABLE_NAME"
                className={`font-mono text-sm ${newKeyError ? "border-destructive focus-visible:ring-destructive" : ""}`}
              />
              {newKeyError && (
                <p className="text-2xs text-destructive">{newKeyError}</p>
              )}
            </div>
            <div className="flex-1 space-y-1">
              <label className="text-2xs text-muted-foreground uppercase tracking-wide">
                Value
              </label>
              <Input
                ref={newValueRef}
                value={newRow.value}
                onChange={(e) => setNewRow({ ...newRow, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmNewRow();
                  if (e.key === "Escape") cancelNewRow();
                }}
                placeholder="value"
                className="font-mono text-sm"
              />
            </div>
            <div className="flex flex-col justify-end gap-1.5 pb-px">
              {/* spacer matches the label height above the inputs */}
              <div className="h-[21px]" />
              <Button size="sm" onClick={confirmNewRow}>
                Add
              </Button>
              <Button size="sm" variant="ghost" onClick={cancelNewRow}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Variables table */}
      {!isEmpty ? (
        <div className="rounded-lg bg-card overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[2fr_3fr_96px] text-2xs font-medium uppercase tracking-wide text-muted-foreground px-4 py-2.5 bg-muted/20 border-b">
            <span>Key</span>
            <span>Value</span>
            <span />
          </div>

          {/* Rows */}
          <div className="divide-y">
            {entries.map(([key, value]) => {
              const sensitive = isSensitive(key);
              const isRevealed = revealed.has(key);
              const isEditing = editingKey === key;

              return (
                <div
                  key={key}
                  className="grid grid-cols-[2fr_3fr_96px] items-center px-4 py-3 gap-3 hover:bg-muted/20 group"
                >
                  <span className="font-mono text-sm truncate">{key}</span>

                  {isEditing ? (
                    <Input
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") confirmEdit(key);
                        if (e.key === "Escape") cancelEdit();
                      }}
                      className="h-8 text-sm font-mono"
                      autoFocus
                    />
                  ) : (
                    <span
                      className="font-mono text-sm text-muted-foreground truncate cursor-pointer hover:text-foreground transition-colors"
                      onClick={() => startEdit(key)}
                      title="Click to edit"
                    >
                      {sensitive && !isRevealed ? (
                        <span className="tracking-widest">••••••••••</span>
                      ) : (
                        value || (
                          <em className="not-italic opacity-40">empty</em>
                        )
                      )}
                    </span>
                  )}

                  <div className="flex items-center gap-0.5 justify-end">
                    {isEditing ? (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-success hover:bg-success-bg"
                          onClick={() => confirmEdit(key)}
                          title="Save"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={cancelEdit}
                          title="Cancel"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        {sensitive ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => toggleReveal(key)}
                            title={isRevealed ? "Hide" : "Show"}
                          >
                            {isRevealed ? (
                              <EyeOff className="h-3.5 w-3.5" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        ) : (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => copyValue(value)}
                            title="Copy value"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive"
                          onClick={() => deleteKey(key)}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        !newRow && (
          <div className="flex flex-col items-center justify-center gap-3 py-10 rounded-lg bg-card border-dashed text-center">
            <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
              <Plus className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">No environment variables</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Add key-value pairs to configure your service's runtime
                environment.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={openNewRow}
              className="mt-1"
            >
              Add your first variable
            </Button>
          </div>
        )
      )}
    </div>
  );
}
